import React, { useState, lazy, Suspense } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link as GatsbyLink } from 'gatsby';
import { AlternativeUpload, IAlternativeUpload } from '@printdeal/alternative-upload';
import {
  Link, Button, Tooltip, useMediaQuery,
} from '@printdeal/ui-library';
import { Price } from '@printdeal/product-selector';
import { FeatureToggle, useExperiment } from '@printdeal/feature-management/react';
import { addDataLayerEntry } from '@printdeal/data-layer-manager';
import { useLocation } from '@gatsbyjs/reach-router';
import { ErrorManagement } from '@printdeal/error-management/react';
import { DELIVERY_TOOLS } from '@printdeal/business-logic';
import OpenPrintagonButton from './OpenPrintagonButton';
import OpenPrintQForm from './OpenPrintQForm';
import { DeliveryToolSettings, FileDeliverySource } from '../../types/file-delivery/FileDelivery';
import { ProductInfo } from '../../types/design';
import { usePageShow } from '../../hooks/usePageShow';

const NColorModal = lazy(() => import(/* webpackChunkName: "ncolor-modal" */ './NColorModal'));

interface Props {
  deliveryToolSettings: DeliveryToolSettings;
  selectedDeliveryTool: string;
  mcpSKU: string;
  mcpAttributes: string;
  productKey: string;
  productVersion?: number;
  productInfo?: ProductInfo[];
  documentReference?: string,
  lineItemId?: string;
  reviewGroupId?: string;
  callbackUrl?: string;
  children: React.ReactNode;
  lineItemDescription?: string;
  lineItemNumber?: string;
  alternativeUploadPath?: string;
  orderId?: string;
  defaultDesignName?: string;
  contentfulProductPageId?: string;
  productName?: string;
  price?: Price;
  formId?: string;
  reviewPath?: string;
  customizedProductId?: string | null;
  redirectUrl?: string;
  className?: string;
  setAltUploadFiles?: (uploads: IAlternativeUpload[]) => void;
  productCode?: string | null;
  designName?: string;
  source: FileDeliverySource;
  setStatus?: (status: 'opening-editor' | 'idle') => void;
  selectedServiceKeys?: string[];
  variantSku?: string;
  orchestrationId?: string;
  deliveryOptionId?: string;
}

const FileDeliveryButton = ({
  deliveryToolSettings,
  selectedDeliveryTool,
  mcpSKU,
  mcpAttributes,
  productKey,
  productVersion,
  productInfo,
  documentReference,
  lineItemId,
  reviewGroupId,
  callbackUrl,
  children,
  lineItemDescription,
  lineItemNumber,
  alternativeUploadPath,
  orderId,
  defaultDesignName,
  contentfulProductPageId,
  productName,
  price,
  formId,
  reviewPath,
  customizedProductId,
  redirectUrl,
  className,
  setAltUploadFiles,
  productCode,
  designName,
  source,
  setStatus,
  selectedServiceKeys,
  variantSku,
  orchestrationId,
  deliveryOptionId,
}: Props) => {
  const intl = useIntl();
  const { locale } = intl;
  const printQMaintenacePages = {
    'nl-NL': '/nl/pages/onderhoud-aan-onze-editor',
    'nl-BE': '/nl/pages/onderhoud-aan-onze-editor',
    'fr-BE': '/fr/pages/service-notre-editeur',
    'fr-FR': '/fr/pages/service-notre-editeur',
  } as Record<string, string>;
  const printQMaintenancePagePath = printQMaintenacePages[locale];

  const {
    experiment: experimentForcePrintQ,
    isInitialized: isForcePrintQInitialized,
  } = useExperiment('pers_force_printq');
  const forcePrintQForTesting = isForcePrintQInitialized && experimentForcePrintQ.variation === 'variant';

  const [showNColorModal, setShowNColorModal] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 901px)');
  const location = useLocation();
  const nColorCount = parseInt(deliveryToolSettings?.ncolorcount || '0', 10);
  const forceNColor = selectedDeliveryTool === 'editor' && nColorCount > 1;
  const printqEditor = forcePrintQForTesting || Boolean(selectedDeliveryTool === 'editor' && Boolean(deliveryToolSettings?.editormode === DELIVERY_TOOLS.PRINTQ));
  const altUploadBeforeOrder = Boolean(selectedDeliveryTool === DELIVERY_TOOLS.ALTERNATIVE_UPLOAD && Boolean(deliveryToolSettings?.altuploadbeforeorder === 'on'));
  const forceAlternativeUpload = Boolean(deliveryToolSettings?.checkmate !== 'on') && Boolean(deliveryToolSettings?.editor !== 'on') && typeof setAltUploadFiles === 'function';
  const uploader = Boolean([DELIVERY_TOOLS.CHECKMATE, DELIVERY_TOOLS.UPLOADER_TOOL].includes(selectedDeliveryTool)
    && deliveryToolSettings.checkmatemode === DELIVERY_TOOLS.CHECKMATE);

  usePageShow(() => setStatus?.('idle'));

  // Disable uploader and printq editor on mobile
  if (!isDesktop && (uploader || printqEditor)) {
    return (
      <Tooltip
        className="pd-w-full"
        trigger={(
          <Button variant="secondary" as={Tooltip.Trigger} className={`${source !== 'PDP' ? 'pd-container' : ''}`} disabled>
            {
              uploader ? (
                <FormattedMessage id="selector.open.uploader.not.available" defaultMessage="Uploader is not available" />
              ) : (
                <FormattedMessage id="selector.open.editor.not.available" defaultMessage="Editor is not available" />
              )
            }
          </Button>
          )}
        placement="top"
      >
        <span>
          {intl.formatMessage({ id: 'file.delivery.use.desktop', defaultMessage: 'Please use desktop version.' })}
        </span>
      </Tooltip>
    );
  }

  if (nColorCount === 1) {
    // N-color modal does not support being opened with n = 1
    ErrorManagement.exception({
      exception: 'N-color modal was selected with 1 color',
      extra: { contentfulProductPageId, productKey, mcpAttributes },
    });
  }

  // If altuploadbeforeorder is set to `on` through MRC
  if (altUploadBeforeOrder && source === 'PDP' && alternativeUploadPath) {
    return (
      <Link
        as={GatsbyLink}
        className="pd-mb-1"
        state={{ productInfo, alternativeUploadPreOrder: true }}
        to={alternativeUploadPath}
      >
        <Button variant="secondary">
          <FormattedMessage id="selector.ncolor.modal.alternativeupload.link" defaultMessage="upload own file" />
        </Button>
      </Link>
    );
  }
  if (forceAlternativeUpload) {
    const setAlternativeUploadFiles = (uploads: IAlternativeUpload[]) => {
      setAltUploadFiles(uploads);
      addDataLayerEntry('alternative_upload', {
        action: 'update_uploads_list',
        location: location.href,
      });
    };
    return (
      <AlternativeUpload
        id="file-delivery"
        name="file-delivery"
        onChange={setAlternativeUploadFiles}
        className="pd-w-full"
        dataLayerData={{ location }}
      />
    );
  }

  if (printqEditor) {
    return (
      <OpenPrintQForm
        productKey={productKey}
        productVersion={productVersion}
        productName={productName}
        mcpSKU={mcpSKU}
        price={price}
        lineItemId={lineItemId}
        reviewGroupId={reviewGroupId}
        lineItemNumber={lineItemNumber}
        orderId={orderId}
        mcpAttributes={mcpAttributes}
        documentReferenceUrl={documentReference}
        reviewPath={reviewPath}
        callbackUrl={callbackUrl}
        customizedProductId={customizedProductId}
        productCode={productCode}
        defaultDesignName={defaultDesignName}
        productInfo={productInfo}
        redirectUrl={redirectUrl}
        className={className}
        setStatus={setStatus}
        editorOverrides={deliveryToolSettings?.editoroverrides}
        source={source}
        selectedServiceKeys={selectedServiceKeys}
        serviceKeys={deliveryToolSettings?.servicekeys}
        variantSku={variantSku}
        formId={formId}
        deliveryOptionId={deliveryOptionId}
      >
        <FeatureToggle id="printq_in_maintenance">
          <FeatureToggle.Enabled>
            {
                source === 'PDP'
                  ? (
                    <>
                      {
                      printQMaintenancePagePath && (
                        <Button
                          as={GatsbyLink}
                          className="pd-w-full"
                          variant="secondary"
                          to={printQMaintenancePagePath}
                        >
                          <FormattedMessage id="selector.open.editor" defaultMessage="Start with designing" />
                        </Button>
                      )
                    }
                    </>
                  )
                  : <>{children}</>
              }
          </FeatureToggle.Enabled>
          <FeatureToggle.Disabled>
            {children}
          </FeatureToggle.Disabled>
        </FeatureToggle>
      </OpenPrintQForm>
    );
  }

  if (forceNColor) {
    return (
      <Suspense fallback={null}>
        <NColorModal
          isOpen={showNColorModal}
          toggleModal={() => setShowNColorModal(!showNColorModal)}
          deliveryToolSettings={deliveryToolSettings}
          selectedDeliveryTool={selectedDeliveryTool}
          mcpSKU={mcpSKU}
          mcpAttributes={mcpAttributes}
          productKey={productKey}
          productVersion={productVersion}
          productInfo={productInfo}
          lineItemId={lineItemId}
          callbackUrl={callbackUrl}
          lineItemDescription={lineItemDescription}
          lineItemNumber={lineItemNumber}
          alternativeUploadPath={alternativeUploadPath}
          orderId={orderId}
          defaultDesignName={defaultDesignName}
          className={className}
          redirectUrl={redirectUrl}
          contentfulProductPageId={contentfulProductPageId}
          source={source}
          selectedServiceKeys={selectedServiceKeys}
        />
        <form
          id={formId}
          onSubmit={(e) => {
            e.preventDefault();
            setShowNColorModal(true);
          }}
        >
          {children}
        </form>
      </Suspense>
    );
  }

  return (
    <OpenPrintagonButton
      deliveryToolSettings={deliveryToolSettings}
      selectedDeliveryTool={selectedDeliveryTool}
      mcpSKU={mcpSKU}
      mcpAttributes={mcpAttributes}
      productKey={productKey}
      productVersion={productVersion}
      productInfo={productInfo}
      formId={formId}
      redirectUrl={redirectUrl}
      documentReference={documentReference}
      lineItemId={lineItemId}
      reviewGroupId={reviewGroupId}
      callbackUrl={callbackUrl}
      lineItemDescription={lineItemDescription}
      lineItemNumber={lineItemNumber}
      alternativeUploadPath={alternativeUploadPath}
      orderId={orderId}
      defaultDesignName={defaultDesignName}
      contentfulProductPageId={contentfulProductPageId}
      className={className}
      designName={designName}
      source={source}
      selectedServiceKeys={selectedServiceKeys}
      setStatus={setStatus}
      variantSku={variantSku}
      orchestrationId={orchestrationId}
      price={price}
    >
      {children}
    </OpenPrintagonButton>
  );
};

export default FileDeliveryButton;
